import { toDateStr } from "./formatting";
import { KO_MODS } from "../constants";
import {
  convertMoney,
  getMoneyContext,
  getPlatformCurrency,
  normalizeCurrency,
} from "./currency";

const isActiveRecord = (record) =>
  !record?.deleted_at && !record?.deletedAt && !record?.DeletedAt;

const findTournament = (tournaments, tournamentId) =>
  tournaments.find((x) => x.id === tournamentId);

const getTournamentCurrency = (tournament, field) => {
  const naturalCurrency = getPlatformCurrency(tournament?.plataforma);
  if (field === "ticket") {
    return normalizeCurrency(tournament?.ticketValueCurrency, naturalCurrency);
  }
  return normalizeCurrency(tournament?.buyinCurrency, naturalCurrency);
};

const getPrizeCurrency = (record, tournament) =>
  normalizeCurrency(
    record?.prizeCurrency,
    getPlatformCurrency(record?.plataforma || tournament?.plataforma)
  );

const convertTournamentValue = (value, tournament, field, moneyContext) =>
  convertMoney(
    Number(value || 0),
    getTournamentCurrency(tournament, field),
    moneyContext.displayCurrency,
    moneyContext.usdBrlRate
  );

const convertPrizeValue = (value, record, tournament, moneyContext) =>
  convertMoney(
    Number(value || 0),
    getPrizeCurrency(record, tournament),
    moneyContext.displayCurrency,
    moneyContext.usdBrlRate
  );

/**
 * Pure calculation: filtered registros based on criteria
 * @param {Array} registros - All registros
 * @param {Array} tournaments - All tournaments
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered registros
 */
export const getFilteredRegistros = (registros, tournaments, filters) => {
  return registros.filter((r) => {
    const t = tournaments.find((x) => x.id === r.torneioId);
    if (filters.buyin && t?.buyin !== filters.buyin) return false;
    if (filters.plataforma && (r.plataforma || t?.plataforma) !== filters.plataforma)
      return false;
    if (filters.modalidade && t?.modalidade !== filters.modalidade) return false;
    if (filters.dataInicio && toDateStr(r.registradoEm) < filters.dataInicio)
      return false;
    if (filters.dataFim && toDateStr(r.registradoEm) > filters.dataFim)
      return false;
    return true;
  });
};

/**
 * Pure calculation: comprehensive statistics
 * @param {Array} source - Registros to calculate from (filtered or all)
 * @param {Array} tournaments - All tournaments
 * @returns {Object} Stats object
 */
export const calculateStats = (source, tournaments, options = {}) => {
  const moneyContext = getMoneyContext(options);
  // Separar registros SAT dos normais
  const normalRegistros = source.filter((r) => {
    const t = findTournament(tournaments, r.torneioId);
    return t?.modalidade !== "SAT";
  });

  // Registros de torneios SAT
  const satRegistros = source.filter((r) => {
    const t = findTournament(tournaments, r.torneioId);
    return t?.modalidade === "SAT";
  });

  // Registros que pagaram buy-in (excluindo via ticket)
  const registrosPago = normalRegistros.filter((r) => !r.isSatellite);
  const totalRegistros = normalRegistros.length + satRegistros.length;
  const itmRegistros = source.reduce((acc, r) => {
    const t = findTournament(tournaments, r.torneioId);
    const prizeValue =
      t?.modalidade === "SAT"
        ? Number(r.tickets || 0)
        : Number(r.premiacao || 0);
    return prizeValue > 0 ? acc + 1 : acc;
  }, 0);

  // Stats gerais
  const total = normalRegistros.length;
  const totalInsc = normalRegistros.reduce(
    (a, r) => a + Number(r.inscricoes || 1),
    0
  );
  const totalGanho = normalRegistros.reduce(
    (a, r) => {
      const t = findTournament(tournaments, r.torneioId);
      return a + convertPrizeValue(r.premiacao, r, t, moneyContext);
    },
    0
  );
  const totalInvestido = registrosPago.reduce((a, r) => {
    const t = findTournament(tournaments, r.torneioId);
    return (
      a +
      (t
        ? convertTournamentValue(t.buyin, t, "buyin", moneyContext) *
          Number(r.inscricoes || 1)
        : 0)
    );
  }, 0);

  const totalInscPago = registrosPago.reduce(
    (a, r) => a + Number(r.inscricoes || 1),
    0
  );

  const abi = totalInscPago > 0 ? totalInvestido / totalInscPago : 0;

  const roi =
    totalInvestido > 0
      ? (((totalGanho - totalInvestido) / totalInvestido) * 100).toFixed(1)
      : "0.0";

  const koCount = registrosPago.reduce((acc, r) => {
    const t = findTournament(tournaments, r.torneioId);
    if (t && KO_MODS.includes(t.modalidade)) {
      return acc + Number(r.inscricoes || 1);
    }
    return acc;
  }, 0);

  const koPct = totalInsc > 0 ? ((koCount / totalInsc) * 100).toFixed(0) : "0";
  const itmPct =
    totalRegistros > 0
      ? ((itmRegistros / totalRegistros) * 100).toFixed(1)
      : "0.0";

  // Stats de satélite
  const satTotalRegistros = satRegistros.length;
  const satTotal = satRegistros.reduce(
    (a, r) => a + Number(r.inscricoes || 1),
    0
  );
  const satTotalInvestido = satRegistros.reduce((a, r) => {
    const t = findTournament(tournaments, r.torneioId);
    return (
      a +
      (t
        ? convertTournamentValue(t.buyin, t, "buyin", moneyContext) *
          Number(r.inscricoes || 1)
        : 0)
    );
  }, 0);

  const satTotalGanho = satRegistros.reduce((a, r) => {
    const t = findTournament(tournaments, r.torneioId);
    const tickets = Number(r.tickets || 0);
    const ticketValue = t
      ? convertTournamentValue(t.valorTicket, t, "ticket", moneyContext)
      : 0;
    return a + tickets * ticketValue;
  }, 0);

  const satLucro = satTotalGanho - satTotalInvestido;
  const satRoi =
    satTotalInvestido > 0
      ? (((satLucro) / satTotalInvestido) * 100).toFixed(1)
      : "0.0";

  return {
    total,
    totalRegistros,
    totalInsc,
    totalGanho,
    totalInvestido,
    abi,
    roi,
    koPct,
    itmPct,
    roiPos: parseFloat(roi) >= 0,
    satTotal,
    satTotalRegistros,
    satTotalInvestido,
    satTotalGanho,
    satLucro,
    satRoi,
    satRoiPos: parseFloat(satRoi) >= 0,
  };
};

/**
 * Pure calculation: chart data (lucro acumulado)
 * @param {Array} source - Registros to calculate from
 * @param {Array} tournaments - All tournaments
 * @param {boolean} showWithoutSat - Exclude satellite tournaments
 * @returns {Object} Chart.js dataset object
 */
export const calculateChartData = (
  source,
  tournaments,
  showWithoutSat,
  options = {}
) => {
  const moneyContext = getMoneyContext(options);
  const activeSource = source.filter(isActiveRecord);
  const sortedByDate = [...activeSource].sort(
    (a, b) => new Date(a.registradoEm) - new Date(b.registradoEm)
  );

  const dateMap = {};
  sortedByDate.forEach((r) => {
    const dateStr = toDateStr(r.registradoEm);
    if (!dateMap[dateStr]) dateMap[dateStr] = [];
    dateMap[dateStr].push(r);
  });

  const dates = Object.keys(dateMap).sort();

  // Calcular lucro COM SAT
  let accumulatedComSat = 0;
  const lucroPorDataComSat = dates.map((date) => {
    const dayRegistros = dateMap[date];
    const dayLucro = dayRegistros.reduce((a, r) => {
      const t = findTournament(tournaments, r.torneioId);
      const isSat = t?.modalidade === "SAT";
      const insc = Number(r.inscricoes || 1);
      const buyin = t ? convertTournamentValue(t.buyin, t, "buyin", moneyContext) : 0;

      const inv = r.isSatellite ? 0 : buyin * insc;
      let prem = 0;
      if (isSat) {
        const tickets = Number(r.tickets || 0);
        const ticketValue = t
          ? convertTournamentValue(t.valorTicket, t, "ticket", moneyContext)
          : 0;
        prem = tickets * ticketValue;
      } else {
        prem = convertPrizeValue(r.premiacao, r, t, moneyContext);
      }
      return a + (prem - inv);
    }, 0);
    accumulatedComSat += dayLucro;
    return accumulatedComSat;
  });

  // Calcular lucro SEM SAT
  let accumulatedSemSat = 0;
  const lucroPorDataSemSat = dates.map((date) => {
    const dayRegistros = dateMap[date];
    const dayLucro = dayRegistros.reduce((a, r) => {
      const t = findTournament(tournaments, r.torneioId);
      const isSat = t?.modalidade === "SAT";

      if (isSat) return a;

      const insc = Number(r.inscricoes || 1);
      const buyin = t ? convertTournamentValue(t.buyin, t, "buyin", moneyContext) : 0;
      const inv = r.isSatellite ? 0 : buyin * insc;
      const prem = convertPrizeValue(r.premiacao, r, t, moneyContext);

      return a + (prem - inv);
    }, 0);
    accumulatedSemSat += dayLucro;
    return accumulatedSemSat;
  });

  // Escolher dados baseado no filtro
  const lucroPorData = showWithoutSat
    ? lucroPorDataSemSat
    : lucroPorDataComSat;
  const labelChart = showWithoutSat
    ? "Lucro Acumulado (sem SAT)"
    : "Lucro Acumulado";
  const finalValue = lucroPorData[lucroPorData.length - 1] || 0;

  return {
    labels: dates.map((d) =>
      new Date(d).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
      })
    ),
    datasets: [
      {
        label: labelChart,
        data: lucroPorData,
        currency: moneyContext.displayCurrency,
        borderColor: finalValue >= 0 ? "#4caf82" : "#e74c3c",
        backgroundColor:
          finalValue >= 0 ? "rgba(76,175,130,0.1)" : "rgba(231,76,60,0.1)",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: "#ce9210",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
      },
    ],
  };
};
